import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Pencil, Play, RefreshCw, TestTube2, Trash2 } from 'lucide-react';
import { fmt } from '@nyce/admin-kit';
import { Button, Chip, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  listToText,
  textToList,
  useAsync,
  useNotice,
} from '../../../components/common';
import { formatDateTime } from '../../../lib/format';
import { useTargets } from '../../../identity/session';
import {
  monitorsFor,
  type BuddySystemRef,
  type Monitor,
  type MonitorAssignment,
  type MonitorNotification,
} from '../../api/monitors';
import { useBuddyWrite } from '../../api/client';
import { buddyPath } from '../../shared';
import { AlertsPanel } from './Alerts';
import { describeCron, validateCron } from './cron';
import {
  COMBINE_MODES,
  NOTIFY_MODES,
  RUN_MODES,
  SOURCE_TYPES,
  fromMonitor,
  systemsFor,
  toInput,
  type MonitorForm,
} from './form';
import { MonitorEditor } from './MonitorEditor';
import { SnapshotView, ToneChip, runSummary, useLookups, useQueryState } from './parts';
import { useRunTest } from './RunTest';

type Tab = 'definition' | 'assignments' | 'runs' | 'alerts' | 'notifications';
const TABS: Tab[] = ['definition', 'assignments', 'runs', 'alerts', 'notifications'];
const label = (options: { value: string; label: string }[], v: string | null | undefined) =>
  options.find((o) => o.value === v)?.label ?? v ?? '—';

type Notify = { success: (s: string) => void; error: (s: string) => void };

/**
 * `/buddy/monitors/:id`: one monitor, with its definition, the systems it is scheduled on, runs, alerts (clear,
 * actions, action log) and the emails it sent. Tab, system filter, open run and open alert live in the URL.
 */
export function BuddyMonitorDetailPage() {
  const { id = '' } = useParams();
  const { active: target } = useTargets();
  const navigate = useNavigate();
  const q = useQueryState();
  const tab = (TABS as string[]).includes(q.get('tab')) ? (q.get('tab') as Tab) : 'definition';
  const api = monitorsFor(target);
  const detail = useAsync(() => api.get(id), [target, id]);
  const { systems, tenants } = useLookups(target);
  const toast = useNotice();
  const runTest = useRunTest(target, systems, toast, () => void detail.reload());
  const [editing, setEditing] = useState<MonitorForm | null>(null);
  const [busy, setBusy] = useState(false);
  const m = detail.data?.monitor;
  // Only the platform admin exists in the hub, so buddy3 always answers canManage = true; honour it anyway.
  const canWrite = useBuddyWrite() && detail.data?.canManage !== false;
  const listPath = buddyPath('/buddy/monitors', target);

  const update = async (body: Parameters<typeof api.update>[1], done: string) => {
    setBusy(true);
    try {
      await api.update(id, body);
      await detail.reload();
      toast.success(done);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const save = async (f: MonitorForm) => {
    const r = toInput(f);
    if ('error' in r) throw new Error(r.error);
    await api.update(id, r.body);
    toast.success(`Saved ${f.name.trim()}.`);
    await detail.reload();
  };
  const remove = async () => {
    try {
      await api.remove(id);
      navigate(listPath);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Page
      title={
        <>
          <Link to={listPath} className="nyk-back">
            Monitors
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          {m?.name ?? '…'}
        </>
      }
      description={
        m && (
          <span className="nyk-inline">
            <Chip size="sm">{m.scope}</Chip>
            <Chip size="sm" className={m.runMode === 'agent' ? 'nyk-tone-blue' : undefined}>
              {m.runMode}
            </Chip>
            <span>
              {m.schedule ?? describeCron(m.cronExpression)}
              {m.systemId ? ` · system ${m.systemId}` : m.tenantId ? ` · tenant ${m.tenantId}` : ''}
            </span>
          </span>
        )
      }
      actions={
        m &&
        canWrite && (
          <div className="bm-head-actions">
            <Switch
              checked={m.enabled}
              disabled={busy}
              onChange={(enabled) => void update({ enabled }, `${m.name} ${enabled ? 'enabled' : 'paused'}.`)}
              label={m.enabled ? 'Enabled' : 'Paused'}
            />
            <Button variant="ghost" icon={<Pencil size={14} />} onClick={() => setEditing(fromMonitor(m))}>
              Edit
            </Button>
            <Button
              variant="ghost"
              icon={<TestTube2 size={14} />}
              loading={runTest.busy === m.id}
              onClick={() => runTest.start(m, 'test')}
            >
              Test
            </Button>
            <Button variant="ghost" icon={<Play size={14} />} onClick={() => runTest.start(m, 'run')}>
              Run now
            </Button>
            <ConfirmButton
              variant="danger"
              icon={<Trash2 size={14} />}
              message={`Delete monitor "${m.name}" with its runs and alerts?`}
              onConfirm={() => void remove()}
            >
              Delete
            </ConfirmButton>
          </div>
        )
      }
    >
      <ErrorBox error={detail.error} />
      {toast.element}
      <Loading loading={detail.loading && !detail.data} />
      {m && (
        <>
          {!m.enabled && (
            <p className="bm-notice">Paused: no assignment runs on schedule until it is enabled.</p>
          )}
          <Tabs
            value={tab}
            onChange={(v) => q.set({ tab: v === 'definition' ? null : v, run: null, alert: null })}
            options={TABS.map((t) => ({
              value: t,
              label:
                t === 'assignments'
                  ? `Assignments (${detail.data?.assignments.length ?? 0})`
                  : t[0].toUpperCase() + t.slice(1),
            }))}
          />
          {tab === 'definition' && <Definition m={m} />}
          {tab === 'assignments' && (
            <Assignments
              target={target}
              monitor={m}
              assignments={detail.data?.assignments ?? []}
              systems={systems}
              canWrite={canWrite}
              notify={toast}
              onRun={(systemId, action) => runTest.start(m, action, systemId)}
              onChanged={() => void detail.reload()}
            />
          )}
          {tab === 'runs' && <Runs target={target} monitor={m} systems={systems} />}
          {tab === 'alerts' && (
            <AlertsPanel
              target={target}
              monitorId={m.id}
              systems={systemsFor(m, systems)}
              canWrite={canWrite}
              notify={toast}
            />
          )}
          {tab === 'notifications' && <Notifications target={target} monitorId={m.id} />}
        </>
      )}
      {editing && (
        <MonitorEditor
          target={target}
          initial={editing}
          tenants={tenants}
          systems={systems}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
      {runTest.element}
    </Page>
  );
}

// ---------------------------------------------------------------------------------------------- definition

function Definition({ m }: { m: Monitor }) {
  return (
    <div className="bd-grid">
      <section className="nyk-card" data-testid="monitor-definition">
        <div className="nyk-card-head">
          <h2>Definition</h2>
        </div>
        {m.description && <p className="bm-desc">{m.description}</p>}
        <dl className="bm-defs">
          <dt>Schedule</dt>
          <dd>
            {m.schedule ?? describeCron(m.cronExpression)}{' '}
            <code className="nyk-code">{m.cronExpression}</code>
          </dd>
          <dt>Run mode</dt>
          <dd>{label(RUN_MODES, m.runMode)}</dd>
          <dt>Combine sources</dt>
          <dd>{label(COMBINE_MODES, m.combineMode)}</dd>
          <dt>Max tool calls</dt>
          <dd>{m.maxToolCalls}</dd>
          {m.logSince && (
            <>
              <dt>Log lookback</dt>
              <dd>{m.logSince}</dd>
            </>
          )}
          <dt>Notify</dt>
          <dd>
            {label(NOTIFY_MODES, m.notifyMode)}
            {m.notifyEmails.length ? ` → ${m.notifyEmails.join(', ')}` : ''}
          </dd>
          {m.createdAt && (
            <>
              <dt>Created</dt>
              <dd>
                {formatDateTime(m.createdAt)}
                {m.createdBy ? ` by ${m.createdBy}` : ''}
                {m.updatedAt ? ` · updated ${formatDateTime(m.updatedAt)}` : ''}
              </dd>
            </>
          )}
        </dl>
        {m.instructions && (
          <>
            <h3 className="bm-section-title">Instructions for the investigation agent</h3>
            <pre className="bm-pre">{m.instructions}</pre>
          </>
        )}
        {m.combineMode === 'llm_gate' && m.gateInstructions && (
          <>
            <h3 className="bm-section-title">Gate instructions</h3>
            <pre className="bm-pre">{m.gateInstructions}</pre>
          </>
        )}
        {m.reportSpec && (
          <>
            <h3 className="bm-section-title">Report spec</h3>
            <pre className="bm-pre">{JSON.stringify(m.reportSpec, null, 2)}</pre>
          </>
        )}
      </section>
      <section className="nyk-card">
        <div className="nyk-card-head">
          <h2>Sources</h2>
          <span className="nyk-ui-hint">{m.sources.length}</span>
        </div>
        {m.savedQueryId ? (
          <p className="nyk-ui-hint">
            {m.fromResponse ? 'Agent made from a chat answer' : 'Scheduled card'} of {m.ownerUserCode ?? '?'}:
            replays saved response <code className="nyk-code">{m.savedQueryId}</code>
            {m.fromResponse && ` · findings = result ${m.resultFrame ?? 'suggested'}`}
            {m.sinceLastRun && ' · since the last run'}
            {m.runAs === 'owner'
              ? ` · runs as ${m.ownerUserCode ?? 'the owner'}`
              : ' · runs as the service user'}
            .
          </p>
        ) : (
          m.sources.map((s) => (
            <div key={s.id ?? s.label} className="bm-source" data-source={s.label}>
              <h3 className="bm-section-title nyk-inline">
                {s.label} <Chip size="sm">{label(SOURCE_TYPES, s.type)}</Chip>
              </h3>
              {s.type === 'llm' ? (
                <pre className="bm-pre">{s.instructions}</pre>
              ) : (
                <pre className="bm-pre">{s.query}</pre>
              )}
              <p className="nyk-ui-hint">
                Entity key: {s.entityKeyColumns?.length ? s.entityKeyColumns.join(', ') : '—'}
                {s.logSince ? ` · lookback ${s.logSince}` : ''}
              </p>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- assignments

interface AssignmentEdit {
  cronOverride: string;
  notifyMode: string;
  notifyEmails: string;
}

function Assignments({
  target,
  monitor: m,
  assignments,
  systems,
  canWrite,
  notify,
  onRun,
  onChanged,
}: {
  target: string;
  monitor: Monitor;
  assignments: MonitorAssignment[];
  systems: BuddySystemRef[];
  canWrite: boolean;
  notify: Notify;
  onRun: (systemId: string, action: 'run' | 'test') => void;
  onChanged: () => void;
}) {
  const api = monitorsFor(target);
  const [adding, setAdding] = useState('');
  const [editing, setEditing] = useState<Record<string, AssignmentEdit>>({});
  const assignable = systemsFor(m, systems).filter((s) => !assignments.some((a) => a.systemId === s.id));
  const systemName = (id: string) => systems.find((s) => s.id === id)?.name ?? id;
  const stopEditing = (systemId: string) =>
    setEditing((cur) => {
      const next = { ...cur };
      delete next[systemId];
      return next;
    });
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    try {
      await fn();
      onChanged();
      if (done) notify.success(done);
      return true;
    } catch (e) {
      notify.error((e as Error).message);
      return false;
    }
  };
  const saveEdit = async (systemId: string, e: AssignmentEdit) => {
    const cron = e.cronOverride.trim();
    const cronError = cron ? validateCron(cron) : null;
    if (cronError) return notify.error(`Cron override: ${cronError}.`);
    // Empty string / list = clear the override (inherit the monitor's).
    const ok = await act(
      () =>
        api.assign(m.id, systemId, {
          cronOverride: cron,
          notifyMode: e.notifyMode,
          notifyEmails: textToList(e.notifyEmails),
        }),
      `Saved ${systemName(systemId)}.`
    );
    if (ok) stopEditing(systemId);
  };

  return (
    <section className="nyk-card">
      <div className="nyk-card-head">
        <h2>Assignments</h2>
        {canWrite && assignable.length > 0 && (
          <div className="bd-toolbar">
            <Select
              aria-label="Add system"
              value={adding}
              onChange={setAdding}
              placeholder="Add system…"
              options={assignable.map((s) => ({ value: s.id, label: `${s.name} (${s.id})` }))}
            />
            <Button
              size="sm"
              disabled={!adding}
              onClick={() =>
                void act(
                  () => api.assign(m.id, adding, { enabled: true }),
                  `Assigned ${systemName(adding)}.`
                ).then((ok) => ok && setAdding(''))
              }
            >
              Add
            </Button>
          </div>
        )}
      </div>
      <p className="nyk-ui-hint">
        A monitor runs on schedule only on the systems it is assigned to; each assignment may override the
        schedule and the notification.
      </p>
      {!assignments.length && (
        <EmptyState
          title="Not assigned to any system yet."
          hint={
            canWrite && assignable.length > 0
              ? 'Add a system above to schedule the monitor there.'
              : undefined
          }
        />
      )}
      {assignments.length > 0 && (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="assignments-table">
            <thead>
              <tr>
                <th>System</th>
                <th>Enabled</th>
                <th>Schedule</th>
                <th>Notify</th>
                <th>Next run</th>
                <th>Last run</th>
              </tr>
            </thead>
            {assignments.map((a) => {
              const e = editing[a.systemId];
              const set = (patch: Partial<AssignmentEdit>) =>
                setEditing((cur) => ({ ...cur, [a.systemId]: { ...e, ...patch } }));
              return (
                <tbody key={a.systemId} className="nyk-row-group" data-assignment={a.systemId}>
                  <tr>
                    <td className="nyk-card-title">
                      <b>{systemName(a.systemId)}</b>
                      <div className="nyk-ui-hint">{a.systemId}</div>
                    </td>
                    <td data-label="Enabled">
                      {canWrite ? (
                        <Switch
                          checked={a.enabled}
                          onChange={(enabled) => void act(() => api.assign(m.id, a.systemId, { enabled }))}
                          label={a.enabled ? 'on' : 'off'}
                        />
                      ) : (
                        <Chip size="sm" className={a.enabled ? 'nyk-tone-green' : undefined}>
                          {a.enabled ? 'on' : 'off'}
                        </Chip>
                      )}
                    </td>
                    <td data-label="Schedule">
                      {e ? (
                        <TextField
                          aria-label={`Cron override ${a.systemId}`}
                          value={e.cronOverride}
                          onChange={(ev) => set({ cronOverride: ev.target.value })}
                          placeholder={`inherit (${m.cronExpression})`}
                          hint={e.cronOverride.trim() ? describeCron(e.cronOverride) : undefined}
                        />
                      ) : a.cronOverride ? (
                        <>
                          {describeCron(a.cronOverride)}
                          <div className="nyk-ui-hint">
                            <code>{a.cronOverride}</code> override
                          </div>
                        </>
                      ) : (
                        <span className="nyk-ui-hint">inherits</span>
                      )}
                    </td>
                    <td data-label="Notify">
                      {e ? (
                        <div className="nyk-inline">
                          <Select
                            aria-label={`Notify mode ${a.systemId}`}
                            value={e.notifyMode}
                            onChange={(v) => set({ notifyMode: v })}
                            options={[{ value: '', label: 'inherit' }, ...NOTIFY_MODES]}
                          />
                          <TextField
                            aria-label={`Notify emails ${a.systemId}`}
                            value={e.notifyEmails}
                            onChange={(ev) => set({ notifyEmails: ev.target.value })}
                            placeholder="inherit recipients"
                          />
                        </div>
                      ) : (
                        <span>
                          {a.notifyMode ? (
                            label(NOTIFY_MODES, a.notifyMode)
                          ) : (
                            <span className="nyk-ui-hint">inherits</span>
                          )}
                          {a.notifyEmails?.length ? ` → ${a.notifyEmails.join(', ')}` : ''}
                        </span>
                      )}
                    </td>
                    <td data-label="Next run">
                      {a.enabled && m.enabled ? formatDateTime(a.nextRunAt) : '—'}
                    </td>
                    <td data-label="Last run">{formatDateTime(a.lastRunAt)}</td>
                  </tr>
                  {canWrite && (
                    <ActionsRow colSpan={6}>
                      {e ? (
                        <>
                          <Button size="sm" onClick={() => void saveEdit(a.systemId, e)}>
                            Save
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => stopEditing(a.systemId)}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Pencil size={14} />}
                          onClick={() =>
                            setEditing((cur) => ({
                              ...cur,
                              [a.systemId]: {
                                cronOverride: a.cronOverride ?? '',
                                notifyMode: a.notifyMode ?? '',
                                notifyEmails: listToText(a.notifyEmails ?? undefined),
                              },
                            }))
                          }
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<TestTube2 size={14} />}
                        onClick={() => onRun(a.systemId, 'test')}
                      >
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Play size={14} />}
                        onClick={() => onRun(a.systemId, 'run')}
                      >
                        Run now
                      </Button>
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        message={`Remove the assignment to ${systemName(a.systemId)}? Its schedule stops; runs and alerts stay.`}
                        onConfirm={() =>
                          void act(() => api.unassign(m.id, a.systemId), `Removed ${systemName(a.systemId)}.`)
                        }
                      >
                        Remove
                      </ConfirmButton>
                    </ActionsRow>
                  )}
                </tbody>
              );
            })}
          </table>
        </TableScroller>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------- runs

function Runs({
  target,
  monitor: m,
  systems,
}: {
  target: string;
  monitor: Monitor;
  systems: BuddySystemRef[];
}) {
  const q = useQueryState();
  const systemId = q.get('system');
  const api = monitorsFor(target);
  const runs = useAsync(
    () => api.runs(m.id, { systemId: systemId || undefined, limit: 100 }),
    [target, m.id, systemId]
  );
  const openId = q.get('run');
  return (
    <section className="nyk-card">
      <div className="nyk-card-head">
        <h2>Runs</h2>
        <div className="bd-toolbar">
          <Select
            aria-label="Run system"
            value={systemId}
            onChange={(v) => q.set({ system: v, run: null })}
            options={[
              { value: '', label: 'All systems' },
              ...systemsFor(m, systems).map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={runs.loading}
            onClick={() => void runs.reload()}
          >
            Refresh
          </Button>
        </div>
      </div>
      <ErrorBox error={runs.error} />
      <Loading loading={runs.loading && !runs.data} />
      {runs.data && !runs.data.length && (
        <EmptyState
          title="No runs yet."
          hint="Runs appear after the schedule fires or someone runs the monitor."
        />
      )}
      {!!runs.data?.length && (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="runs-table">
            <thead>
              <tr>
                <th>Started</th>
                <th>System</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Rows / alerts</th>
                <th className="nyk-ui-num">Tokens</th>
                <th className="nyk-ui-num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map((r) => (
                <tr key={r.id} data-run={r.id}>
                  <td className="nyk-card-title">
                    <button type="button" className="nyk-link" onClick={() => q.set({ run: r.id })}>
                      {formatDateTime(r.startedAt)}
                    </button>
                  </td>
                  <td data-label="System">{r.systemId}</td>
                  <td data-label="Trigger">{r.triggerSource}</td>
                  <td data-label="Status">
                    <ToneChip kind="run" value={r.status} />
                    {r.error && <div className="nyk-ui-hint bm-clip">{r.error}</div>}
                  </td>
                  <td data-label="Rows / alerts">{runSummary(r)}</td>
                  <td className="nyk-ui-num" data-label="Tokens">
                    {r.totalTokens ? fmt(r.totalTokens) : '—'}
                  </td>
                  <td className="nyk-ui-num" data-label="Cost">
                    {r.estimatedCost ? `$${r.estimatedCost.toFixed(4)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      )}
      {openId && <RunDialog target={target} runId={openId} onClose={() => q.set({ run: null })} />}
    </section>
  );
}

/** One run, loaded by id (`?run=` can be shared): status, the model's proposal, the trigger snapshot. */
function RunDialog({ target, runId, onClose }: { target: string; runId: string; onClose: () => void }) {
  const run = useAsync(() => monitorsFor(target).runDetail(runId), [target, runId]);
  const r = run.data;
  const p = r?.proposal;
  return (
    <Dialog
      open
      onClose={onClose}
      title={r ? `Run on ${r.systemId}, ${formatDateTime(r.startedAt)}` : 'Run'}
      maxWidth={880}
    >
      <ErrorBox error={run.error} />
      <Loading loading={run.loading && !r} />
      {r && (
        <div data-testid="run-detail">
          <p className="nyk-inline">
            <ToneChip kind="run" value={r.status} />
            <span className="nyk-ui-hint">
              {[
                r.triggerSource,
                runSummary(r),
                r.model,
                r.toolCallCount ? `${r.toolCallCount} tool call(s)` : null,
                r.totalTokens ? `${fmt(r.totalTokens)} tokens` : null,
                r.finishedAt ? `finished ${formatDateTime(r.finishedAt)}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </p>
          {r.error && <div className="bm-error">{r.error}</div>}
          {p && (
            <section>
              <h3 className="bm-section-title nyk-inline">
                Proposal <ToneChip kind="severity" value={p.severity} />
              </h3>
              <p className="bm-finding">{p.summary}</p>
              {(p.items ?? []).map((it) => (
                <details key={it.entityKey} className="nyk-group bm-source" open>
                  <summary>
                    <b>{it.title}</b> <ToneChip kind="severity" value={it.severity} />{' '}
                    <span className="nyk-ui-hint">{it.entityKey}</span>
                  </summary>
                  <p className="bm-finding">{it.finding}</p>
                  {it.proposedActions?.length > 0 && (
                    <ul>
                      {it.proposedActions.map((a, i) => (
                        <li key={i}>
                          <Chip size="sm">{a.type.replace('_', ' ')}</Chip> {a.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </details>
              ))}
            </section>
          )}
          <h3 className="bm-section-title">Trigger snapshot{r.snapshotTruncated ? ' (truncated)' : ''}</h3>
          <SnapshotView value={r.triggerSnapshot} />
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------- notifications

function Notifications({ target, monitorId }: { target: string; monitorId: string }) {
  const list = useAsync(() => monitorsFor(target).notifications(monitorId, 100), [target, monitorId]);
  const [open, setOpen] = useState<MonitorNotification | null>(null);
  return (
    <section className="nyk-card">
      <div className="nyk-card-head">
        <h2>Notifications</h2>
        <span className="nyk-ui-hint">every email of this monitor: sent, mocked or failed</span>
      </div>
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !list.data.length && <EmptyState title="No emails yet." />}
      {!!list.data?.length && (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="notifications-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>When</th>
                <th>System</th>
                <th>Recipients</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((n) => (
                <tr key={n.id} data-notification={n.id}>
                  <td className="nyk-card-title">
                    <button type="button" className="nyk-link" onClick={() => setOpen(n)}>
                      {n.subject || '(no subject)'}
                    </button>
                  </td>
                  <td data-label="When">{formatDateTime(n.createdAt)}</td>
                  <td data-label="System">{n.systemId}</td>
                  <td data-label="Recipients">{n.recipients.join(', ')}</td>
                  <td data-label="Status">
                    <ToneChip kind="mail" value={n.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      )}
      {open && (
        <Dialog open onClose={() => setOpen(null)} title={open.subject || 'Email'} maxWidth={820}>
          <p className="nyk-inline">
            <ToneChip kind="mail" value={open.status} />
            <span className="nyk-ui-hint">
              {formatDateTime(open.createdAt)} · {open.systemId} · to {open.recipients.join(', ')}
            </span>
          </p>
          {open.htmlBody ? (
            // The mail as rendered: a sandbox without scripts, forms or navigation.
            <iframe
              title="Email body"
              className="bm-mail"
              sandbox=""
              srcDoc={open.htmlBody}
              data-testid="mail-body"
            />
          ) : (
            <pre className="bm-pre" data-testid="mail-body">
              {open.textBody ?? ''}
            </pre>
          )}
        </Dialog>
      )}
    </section>
  );
}
